import type { ReactElement } from "react";
import { cn } from "@/lib/utils";
import type { PillProvider } from "./provider-pill-providers";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./base/tooltip";

interface ProviderPillProps {
  provider: PillProvider;
  /** Hide the wordmark and render the glyph alone. */
  iconOnly?: boolean;
  className?: string;
}

interface ProviderConfig {
  label: string;
  Icon: (props: { className?: string }) => ReactElement;
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="#000000"
        d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
      />
    </svg>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function StripeIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="#635BFF"
        d="M13.3 10.3c-1.5-.6-2.4-1-2.4-1.7 0-.6.5-1 1.4-1 1.6 0 3.3.6 4.4 1.2l.6-3.9c-.9-.4-2.6-1-4.9-1-1.6 0-3 .4-4 1.2-1 .8-1.6 2-1.6 3.5 0 2.6 1.6 3.7 4.2 4.7 1.7.6 2.2 1 2.2 1.7 0 .6-.6 1-1.6 1-1.3 0-3.4-.6-4.8-1.4l-.6 3.9c1.2.7 3.4 1.4 5.7 1.4 1.7 0 3.2-.4 4.2-1.2 1.1-.9 1.7-2.2 1.7-3.9 0-2.7-1.6-3.8-4.1-4.5z"
      />
    </svg>
  );
}

/** The CPOS mark: a two-tone open ring with the amber dot in its gap. */
function CposIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        d="M12 4.44A6.6 6.6 0 1 0 12 17.64"
        fill="none"
        stroke="#0F0A6E"
        strokeWidth="4"
        strokeLinecap="butt"
      />
      <path
        d="M12 17.64A6.6 6.6 0 0 0 18.6 11.04"
        fill="none"
        stroke="#3B4BF5"
        strokeWidth="4"
        strokeLinecap="butt"
      />
      <circle cx="16.3" cy="6.84" r="2.76" fill="#F5A81C" />
    </svg>
  );
}

const PROVIDERS: Record<PillProvider, ProviderConfig> = {
  apple: { label: "Apple", Icon: AppleIcon },
  google: { label: "Google", Icon: GoogleIcon },
  stripe: { label: "Stripe", Icon: StripeIcon },
  cpos: { label: "CPOS", Icon: CposIcon },
};

/**
 * Brand pill for the party a record originated from — the store that reported a
 * purchase, or the processor that produced an event.
 */
export function ProviderPill({
  provider,
  iconOnly = false,
  className,
}: ProviderPillProps) {
  // eslint-disable-next-line security/detect-object-injection -- controlled enum values
  const { label, Icon } = PROVIDERS[provider];

  const pill = (
    <span
      className={cn(
        "border-border bg-card text-foreground inline-flex items-center gap-1.5 rounded-full border py-0.5 text-xs font-medium shadow-xs",
        iconOnly ? "px-1" : "px-2",
        className,
      )}
      aria-label={iconOnly ? label : undefined}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      {!iconOnly && label}
    </span>
  );

  // Only the icon-only variant needs one: the wordmark is already the label.
  if (!iconOnly) return pill;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{pill}</TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
